import { cn } from '../lib/cn';
import { formatCount, formatCurrency } from '../lib/format';

export interface RankedRow {
  label: string;
  amount: string;
  /** Secondary figure, e.g. units or transaction count. */
  detail?: string | undefined;
}

/**
 * Ranked magnitude across categories.
 *
 * Rendered as a real table with the bar inside a cell, which gives the table
 * view for free rather than as a separate affordance — the numbers are always
 * present and the bar is a reading aid, not the data.
 *
 * One measure, so one hue. Colouring each department differently would imply the
 * colours mean something; they would only be decoration, and the reader has to
 * check the legend to learn they mean nothing.
 *
 * `measure` says what `amount` is counted in. It is not cosmetic: the bars are
 * scaled by `amount`, so whatever this formats is also what the ranking means,
 * and a chart whose bars say units while its figures say pounds would be lying
 * about which one it sorted on.
 */
export function RankedBars({
  rows,
  caption,
  measure = 'currency',
  emptyMessage = 'Nothing to show for this period.',
}: {
  rows: RankedRow[];
  caption: string;
  measure?: 'currency' | 'count';
  emptyMessage?: string;
}): React.JSX.Element {
  if (rows.length === 0) {
    return <p className="px-3 py-6 text-center text-xs text-ink-faint">{emptyMessage}</p>;
  }

  // Scaled against the largest magnitude so a negative row is comparable.
  const max = Math.max(...rows.map((row) => Math.abs(Number(row.amount))), 0);

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <caption className="sr-only">{caption}</caption>
        <tbody>
          {rows.map((row) => {
            const value = Number(row.amount);
            const width = max > 0 ? (Math.abs(value) / max) * 100 : 0;

            return (
              <tr key={row.label} className="border-b border-rule last:border-0">
                <th
                  scope="row"
                  className="max-w-[10rem] truncate py-1.5 pl-3 text-left text-xs font-normal"
                  title={row.label}
                >
                  {row.label}
                </th>
                <td className="w-1/2 py-1.5 pr-2 pl-2">
                  <span
                    aria-hidden
                    className={cn(
                      'block h-1 rounded-[2px]',
                      value < 0 ? 'bg-down' : 'bg-accent',
                    )}
                    style={{ width: `${width}%` }}
                  />
                </td>
                {row.detail !== undefined && (
                  <td className="qs-figure py-1.5 pr-2 text-right text-2xs text-ink-faint">
                    {row.detail}
                  </td>
                )}
                <td className="qs-figure py-1.5 pr-3 text-right text-xs">
                  {measure === 'count' ? formatCount(row.amount) : formatCurrency(row.amount)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
